import {
  getDashboardLink,
  getEmojiFromDashboard,
  trackDashboardEvent,
} from '@modules/dashboard/helpers';
import { DashboardActionType } from '@modules/dashboard/store/dashboardActionType';
import { useDashboardStore } from '@modules/dashboard/store/useDashboardStore';
import {
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { DashboardListItemViewFragment } from 'generated/graphql';
import { useGetUser } from 'v2/core/services/user';

interface CreateNewDashboardTemplateListProps {
  templates: DashboardListItemViewFragment[];
  isLoadingTemplates: boolean;
  selectedTemplate?: DashboardListItemViewFragment;
  onTemplateSelect: (template: DashboardListItemViewFragment) => void;
}

export const CreateNewDashboardTemplateList = ({
  templates,
  isLoadingTemplates,
  selectedTemplate,
  onTemplateSelect,
}: CreateNewDashboardTemplateListProps) => {
  const {
    tenant: { slug: tenantSlug },
  } = useGetUser();

  const dispatch = useDashboardStore(({ dispatch }) => dispatch);

  if (isLoadingTemplates) {
    return (
      <Stack spacing={0.5}>
        {[...Array(3)].map((_, index) => (
          <Skeleton key={index} variant="rectangular" height={48} />
        ))}
      </Stack>
    );
  }

  if (templates.length === 0) {
    return (
      <Typography variant="caption" color="text.disabled">
        No templates created yet
      </Typography>
    );
  }

  const onItemSelect = (template: DashboardListItemViewFragment) => {
    onTemplateSelect(template);
    // Close the QB when navigating to other template
    dispatch({
      type: DashboardActionType.SET_VISUAL,
      payload: {
        shouldShowQueryBuilder: false,
      },
    });
    trackDashboardEvent({
      eventName: 'Template selected on Create New Dashboard Page',
      templateLink: getDashboardLink(template, tenantSlug),
    });
  };

  const TemplateListItem = ({
    template,
  }: {
    template: DashboardListItemViewFragment;
  }) => (
    <ListItemButton
      selected={selectedTemplate?.id === template.id}
      onClick={() => onItemSelect(template)}
      title={template.title}
      aria-label="Select Template button"
    >
      <ListItemIcon sx={{ minWidth: '36px', color: 'unset' }}>
        {getEmojiFromDashboard(template)}
      </ListItemIcon>
      <ListItemText
        primary={template.title}
        primaryTypographyProps={{ noWrap: true }}
      />
    </ListItemButton>
  );

  return (
    <List>
      {templates.map((template) => (
        <TemplateListItem key={template.id} template={template} />
      ))}
    </List>
  );
};
